import { useEffect, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { Loader } from "../../../Common/Loader";
import PaginatedDropdown from "../../../Common/PaginatedDropdown";
import { ConfirmModel } from "../../../Model/ConfirmModel";
import { ErrorModel } from "../../../Model/ErrorModel";
import { SuccessModel } from "../../../Model/SuccessModel";
import { setUnsavedChanges } from "../../../Redux/Slice/Common/SelectedTabSlice";
import { getCustomerList } from "../../../Redux/Slice/Customers/CustomerListSlice";
import { addTopUpPlan } from "../../../Redux/Slice/TopUp/CreateTopUpPlanSlice";
import { updateTopUpPlan } from "../../../Redux/Slice/TopUp/UpdateTopUpPlanSlice";
import { getTopUpPlanDetails } from "../../../Redux/Slice/TopUp/GetTopUpPlanDetailsSlice";

const AddTopupPlan = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const planId = location?.pathname?.split("/")[2];
  const isEdit = !!planId;
  const [errorModel, setErrorModel] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [successModel, setSuccessModel] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [hasChanged, setHasChanged] = useState(false);
  const [hasChangedModel, setHasChangedModel] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    price: 0,
    isPublic: false,
    numberOfCredits: 0,
    allotToCustomer: [],
    description: "",
  });
  const [formError, setFormError] = useState({
    nameError: "",
  });

  const getPlanData = async () => {
    setIsLoading(true);
    try {
      if (isEdit) {
        const result = await dispatch(getTopUpPlanDetails(planId));
        if (
          getTopUpPlanDetails.fulfilled.match(result) &&
          result?.payload?.success === true
        ) {
          const planData = result?.payload?.data;
          const allotedCustomers = Array.isArray(planData?.allotedUser)
            ? planData?.allotedUser?.map((customer) => {
                const adminName =
                  customer?.firstName || customer?.lastName
                    ? `${customer?.firstName} ${customer?.lastName}`
                    : "";

                const label =
                  (customer?.customer?.companyName
                    ? customer?.customer?.companyName + (adminName ? " - " : "")
                    : "") + adminName;
                return {
                  label: label,
                  value: customer?.id,
                };
              })
            : [];

          setFormData((prev) => ({
            ...prev,
            name: planData?.name,
            price: planData?.price,
            numberOfCredits: planData?.numberOfCredits,
            allotToCustomer: allotedCustomers,
            description: planData?.description,
            isPublic: planData?.isPublic ?? false,
          }));
        }
      }
    } catch (error) {
      console.log(error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleChange = (e) => {
    let { value, name } = e.target;
    setHasChanged(true);
    if (name == "name") {
      value = String(value).trimStart();
      let capitalized = value.charAt(0).toString().toUpperCase();
      value = capitalized + value.substring(1);
      if (String(value).length < 3 && String(value).length > 0) {
        setFormError((prev) => ({
          ...prev,
          [`${name}Error`]: "Plan name should have minimum 3 character",
        }));
      } else {
        setFormError((prev) => ({
          ...prev,
          [`${name}Error`]: "",
        }));
      }
    }
    if (
      ((name === "price" || name === "numberOfCredits") && value >= 0) ||
      value == ""
    ) {
      if (value.startsWith("0")) {
        value = value.slice(1);
      }
      setFormData({ ...formData, [name]: value });
    } else if (name != "price" && name != "numberOfCredits") {
      setFormData({ ...formData, [name]: value });
    }
    dispatch(setUnsavedChanges(true));
  };

  const handleSubmit = async () => {
    let body = {
      name: formData.name,
      price: formData.price,
      numberOfCredits: formData?.numberOfCredits,
      description: formData.description,
      allotedUser: !formData?.isPublic
        ? formData.allotToCustomer?.map((customer) => customer?.value)
        : [],
      isPublic: formData.isPublic,
    };

    dispatch(setUnsavedChanges(false));
    if (isEdit) {
      setIsLoading(true);
      const result = await dispatch(updateTopUpPlan({ id: planId, body }));
      if (
        updateTopUpPlan.fulfilled.match(result) &&
        result?.payload?.success === true
      ) {
        setIsLoading(false);
        setSuccessMessage("Top-Up Plan updated successfully");
        setSuccessModel(true);
        setTimeout(() => {
          setSuccessModel(false);
          navigate("/plans#topup");
        }, 1500);
      } else {
        setIsLoading(false);
        setErrorMessage(result?.payload?.error?.message);
        setErrorModel(true);
        setTimeout(() => {
          setErrorModel(false);
        }, 1500);
      }
    } else {
      setIsLoading(true);
      const result = await dispatch(addTopUpPlan(body));
      if (
        addTopUpPlan.fulfilled.match(result) &&
        result?.payload?.success === true
      ) {
        setIsLoading(false);
        setSuccessMessage("Top-Up Plan added successfully");
        setSuccessModel(true);
        setTimeout(() => {
          setSuccessModel(false);
          navigate("/plans#topup");
        }, 1500);
      } else {
        //TODO: error popup here
        setIsLoading(false);
        setErrorMessage(result?.payload?.error?.message);
        setErrorModel(true);
        setTimeout(() => {
          setErrorModel(false);
        }, 1500);
      }
    }
  };

  const onAllotCustomer = (customerList) => {
    setHasChanged(true);
    setFormData((prev) => ({
      ...prev,
      allotToCustomer: customerList,
    }));
    dispatch(setUnsavedChanges(true));
  };

  const loadCustomerOptions = async (
    search,
    loadedOptions,
    { page, limit }
  ) => {
    const result = await dispatch(
      getCustomerList({
        searchText: search,
        page,
        limit,
        sortBy: "companyName",
        sortOrder: "ASC",
      })
    );
    const totalPages = result?.payload?.pagination?.totalPages;
    const options = Array.isArray(result?.payload?.data)
      ? result?.payload?.data?.map((customer) => {
          const adminName =
            customer?.admin?.firstName || customer?.admin?.lastName
              ? `${customer?.admin?.firstName} ${customer?.admin?.lastName}`
              : "";

          const label =
            (customer?.companyName
              ? customer?.companyName + (adminName ? " - " : "")
              : "") + adminName;
          return {
            label: label,
            value: customer?.admin?.id,
          };
        })
      : [];

    return {
      options: options,
      hasMore:
        !options || !options?.length || page == totalPages ? false : true,
      additional: {
        page: page + 1,
      },
    };
  };

  const isDisabled = useMemo(() => {
    return (
      !hasChanged ||
      formError.nameError ||
      formData?.name?.trim() === "" ||
      !formData.price ||
      formData.price === "0" ||
      formData.numberOfCredits === 0 ||
      formData.numberOfCredits === "0" ||
      formData.numberOfCredits === ""
    );
  }, [formData, formError, hasChanged]);

  useEffect(() => {
    getPlanData();
  }, [planId]);

  return (
    <div className="page__body">
      {errorModel ? <ErrorModel message={errorMessage} /> : ""}
      {successModel ? <SuccessModel message={successMessage} /> : ""}
      {isLoading ? <Loader /> : ""}
      {hasChangedModel ? (
        <ConfirmModel
          onAccept={() => {
            dispatch(setUnsavedChanges(false));
            navigate("/plans#topup");
          }}
          yes={"Yes"}
          no={"No"}
          onReject={() => {
            setHasChangedModel(false);
          }}
          title="Update Changes!"
          message="Changes made will not be saved. Are you sure you want to leave the page?"
        />
      ) : null}
      <div className="details">
        <div className="form">
          <section className="form__section">
            <div className="formRow formRow__2">
              <div className="formCol">
                <div className="form__Field">
                  <label for="planName" className="form__label">
                    Plan Name <span style={{ color: "red" }}> * </span>
                  </label>
                  <input
                    type="text"
                    id="planName"
                    className="form__input form__input--sm"
                    name="name"
                    maxLength={30}
                    value={formData.name}
                    onChange={handleChange}
                    placeholder="Plan Name"
                  />
                </div>
                {formError?.nameError ? (
                  <div className="form__error">{formError?.nameError}</div>
                ) : null}
              </div>
              <div className="formCol">
                <div className="form__Field">
                  <label for="planPrice" className="form__label">
                    Plan Price <span style={{ color: "red" }}> * </span>
                  </label>
                  <input
                    type="number"
                    id="planPrice"
                    className="form__input form__input--sm"
                    placeholder="$0"
                    name="price"
                    value={formData.price}
                    onChange={handleChange}
                  />
                </div>
              </div>
            </div>
            <div className="formRow formRow__2">
              <div className="formCol">
                <div className="form__Field">
                  <label for="noCredits" className="form__label">
                    No. of Credits <span style={{ color: "red" }}> * </span>
                  </label>
                  <input
                    type="number"
                    id="noCredits"
                    className="form__input form__input--sm"
                    placeholder="0"
                    name="numberOfCredits"
                    value={formData.numberOfCredits}
                    onChange={handleChange}
                  />
                </div>
              </div>
              <div className="formCol">
                <div className="form__Field">
                  <label for="noCredits" className="form__label">
                    Plan Public
                  </label>
                  <div className="form__switchBtn">
                    <div className="form__switchBtn__option">
                      <div className="switchBtn">
                        <input
                          type="checkbox"
                          className="toggle"
                          id="toggle-isPublic"
                          checked={formData?.isPublic}
                          onChange={(e) => {
                            setFormData((prev) => ({
                              ...prev,
                              isPublic: e.target.checked,
                            }));
                            setHasChanged(true);
                            dispatch(setUnsavedChanges(true));
                          }}
                        />
                        <label for="toggle-isPublic">
                          <span className="on">ON</span>
                          <span className="off">OFF</span>
                        </label>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="formRow formRow__2">
              <div className="formCol">
                <div className="form__Field">
                  <label for="planDescription" className="form__label">
                    Plan Description
                  </label>
                  <textarea
                    type="date"
                    id="description"
                    className="form__textarea form__textarea--sm"
                    placeholder="Write here..."
                    name="description"
                    value={formData.description}
                    onChange={handleChange}
                  ></textarea>
                </div>
              </div>
              {!formData.isPublic ? (
                <div className="formCol">
                  <div className="form__Field">
                    <label for="noCredits" className="form__label">
                      Allot to Customer
                    </label>
                    <PaginatedDropdown
                      value={formData.allotToCustomer}
                      loadOptions={loadCustomerOptions}
                      onChange={onAllotCustomer}
                      isMulti
                    />
                  </div>
                </div>
              ) : null}
            </div>

            <div className="btn__group">
              <button
                className="btn btn__white"
                type="button"
                onClick={() => {
                  if (hasChanged) {
                    setHasChangedModel(true);
                  } else {
                    dispatch(setUnsavedChanges(false));
                    navigate("/plans#topup");
                  }
                }}
              >
                Cancel
              </button>
              <button
                className="btn btn__primary"
                type="submit"
                disabled={isDisabled}
                onClick={handleSubmit}
              >
                {isEdit ? "Update" : "Save"}
              </button>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
};

export default AddTopupPlan;
